import { StatusType } from '@oy/types';
import { Colors } from '@oy/uicore';
import { Text } from '@oy/uidesktop';
import { CSSProperties } from 'react';
import { StatusPill } from 'src/components';
import { useTranslate } from 'src/hooks';
import styles from './CreditCard.module.css';
import CreditCardWarning from './CreditCardWarning';
import cimbNiagaIcon from './icons/cimb-niaga.svg';
import masterCardIcon from './icons/mastercard.svg';
import oyWhiteIcon from './icons/oywhite.svg';

export enum CreditCardColor {
  Blue = 'Blue',
  Orange = 'Orange',
  Gray = 'Gray',
  Pink = 'Pink',
  Green = 'Green',
  Purple = 'Purple',
  Physical = 'Physical',
}
export enum CreditCardSize {
  Regular = 'Regular',
  Large = 'Large',
  Small = 'Small',
}

const itemSizes: Record<string, any> = {
  cimb: {
    Large: '30px',
    Regular: '20px',
    Small: '17px',
  },
  oywhite: {
    Large: '50px',
    Regular: '30px',
    Small: '25px',
  },
  mastercard: {
    Large: '50px',
    Regular: '40px',
    Small: '30px',
  },
  cardName: {
    Large: Text.Style.BodyXlSemibold,
    Regular: Text.Style.BodyMdSemibold,
    Small: Text.Style.BodySmSemibold,
  },
  cardNameTitle: {
    Large: Text.Style.BodySmRegular,
    Regular: Text.Style.BodyXsRegular,
    Small: Text.Style.BodyXsRegular,
  },
  cardNumber: {
    Large: Text.Style.HeadingMd,
    Regular: Text.Style.HeadingXs,
    Small: Text.Style.HeadingXs,
  },
};

const getSize = (item: string, size: CreditCardSize) => {
  return itemSizes[item][size];
};

type CreditCardProps = {
  onClick: () => void;
  cardName: string;
  cardExpiryDate: string;
  cardNumber: string;
  cardStatus: StatusType | '';
  cardColor: CreditCardColor;
  cardSize?: CreditCardSize;
  selected?: boolean;
  hideNumber?: boolean;
  showWarning?: boolean;
  showExpiry?: boolean;
  clickable?: boolean;
  statusId?: string;
  containerId?: string;
  hideStatus?: boolean;
  style?: CSSProperties;
  showIcons?: boolean;
  className?: string;
};

export default function CreditCard({
  onClick,
  cardName = '',
  cardNumber = '',
  cardStatus,
  cardColor = CreditCardColor.Gray,
  cardSize = CreditCardSize.Regular,
  cardExpiryDate,
  style = {},
  selected = false,
  showWarning = false,
  hideNumber = true,
  clickable = false,
  showExpiry = false,
  containerId = '',
  statusId = '',
  hideStatus = false,
  showIcons = false,
  className,
}: CreditCardProps) {
  const { getTranslation } = useTranslate();
  return (
    <div
      className={`${styles.creditCardContainer} ${styles[cardColor]} ${
        className ? className : ''
      } ${styles[cardSize]} ${selected ? styles.outlined : ''}`}
      style={{ cursor: clickable ? 'pointer' : 'default', ...style }}
      onClick={onClick}
      id={containerId}
    >
      {showWarning && (
        <>
          <div className={styles.creditCardWarning}>
            <CreditCardWarning />
          </div>
        </>
      )}

      {(cardStatus != '' || showIcons) && (
        <>
          <div className="d-flex flex-row justify-content-between">
            <img src={cimbNiagaIcon} height={getSize('cimb', cardSize)} />
          </div>

          <div className={`${styles.creditOyLogo}`}>
            <img src={oyWhiteIcon} height={getSize('oywhite', cardSize)} />
          </div>
        </>
      )}

      <div>
        <div style={{ width: 'max-content' }} id={statusId}>
          {cardStatus != '' && !hideStatus && (
            <StatusPill status={cardStatus} />
          )}
        </div>
      </div>

      <div className="d-flex align-items-start" style={{ height: '25px' }}>
        <Text textStyle={getSize('cardNumber', cardSize)} color={Colors.WHITE}>
          {hideNumber && cardNumber && '●●●● ●●●● ●●●● ' + cardNumber.slice(-4)}
          {!hideNumber && cardNumber.match(/.{1,4}/g)?.join(' ')}
        </Text>
      </div>

      <div className="d-flex flex-row justify-content-between align-items-center">
        <div className="d-flex flex-column justify-content-between aliten-items-between">
          <div className="mb-1">
            {cardName != '' && (
              <Text
                textStyle={getSize('cardNameTitle', cardSize)}
                color={Colors.WHITE}
              >
                {getTranslation('corporateCard_common_cardName')}
              </Text>
            )}
          </div>
          <div>
            <Text
              textStyle={getSize('cardName', cardSize)}
              color={Colors.WHITE}
            >
              {cardName}
            </Text>
          </div>
        </div>
        {cardStatus != '' && showExpiry && (
          <div className="d-flex flex-column justify-content-between aliten-items-between">
            <div className="mb-1">
              {cardExpiryDate != '' && (
                <Text
                  textStyle={getSize('cardNameTitle', cardSize)}
                  color={Colors.WHITE}
                >
                  Expiry Date
                </Text>
              )}
            </div>
            <div>
              <Text
                textStyle={getSize('cardName', cardSize)}
                color={Colors.WHITE}
              >
                {cardExpiryDate}
              </Text>
            </div>
          </div>
        )}
        <div>
          {(cardStatus != '' || showIcons) && (
            <img
              src={masterCardIcon}
              height={getSize('mastercard', cardSize)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
